import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, Link, useToast,
} from '@chakra-ui/react';
import {
  ImmCanalizacion, crearCanalizacion, actualizarCanalizacion,
  descargarPdfCanalizacion, fileUrl,
} from '../../../api/immApi';
import {
  SectionTitle, Field, ExpedienteSelect, IMM_COLOR, IMM_COLOR_HOVER,
  fmt, TIPO_DECISION_LABEL,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmCanalizacion;
  onGuardado?: (c: ImmCanalizacion) => void;
  onCancelar?: () => void;
  onActualizado?: () => void;
}

const ESTATUS_OPCIONES = ['Emitida', 'Enviada', 'Atendida', 'Cancelada'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioCanalizacion: React.FC<Props> = ({ modo, data, onGuardado, onCancelar, onActualizado }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);
  const [rutaPdf, setRutaPdf] = useState<string | null | undefined>(data?.rutaPdfGenerado);

  const [f, setF] = useState<Partial<ImmCanalizacion>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    motivo: data?.motivo || '',
    descripcionHecho: data?.descripcionHecho || '',
    solicitud: data?.solicitud || '',
    dependenciaDestino: data?.dependenciaDestino || '',
    observaciones: data?.observaciones || '',
    decisionDenuncia: data?.decisionDenuncia || '',
    firmaUsuaria: false,
    firmaPersonalImm: false,
    estatus: data?.estatus || 'Emitida',
  });

  const set = (campo: keyof ImmCanalizacion, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!expedienteId) {
      toast({ title: 'Selecciona un expediente', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmCanalizacion> = { ...f, expedienteId };
      const res = modo === 'edit' && data
        ? await actualizarCanalizacion(data.id, payload)
        : await crearCanalizacion(payload);
      toast({ title: modo === 'edit' ? 'Canalización actualizada' : 'Canalización registrada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la canalización.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const handleVerPdf = async () => {
    if (!data) return;
    setGenerandoPdf(true);
    try {
      await descargarPdfCanalizacion(data.id);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo abrir el PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la canalización</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Folio" value={data.folio} />
          <Field label="Expediente" value={data.expediente?.folio ?? ('#' + data.expedienteId)} />
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Dependencia destino" value={data.dependenciaDestino} />
          <Field label="Decisión de denuncia" value={data.decisionDenuncia ? (TIPO_DECISION_LABEL[data.decisionDenuncia] || data.decisionDenuncia) : '—'} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>

        <SectionTitle>Detalle</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Motivo" value={data.motivo} />
          <Field label="Descripción del hecho" value={data.descripcionHecho} />
          <Field label="Solicitud" value={data.solicitud} />
          <Field label="Observaciones" value={data.observaciones} />
        </SimpleGrid>

        <SectionTitle>Documento (oficio)</SectionTitle>
        <HStack spacing={3} flexWrap="wrap">
          <Button size="sm" bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleVerPdf} isLoading={generandoPdf} loadingText="Abriendo...">
            Ver / Descargar PDF
          </Button>
          {rutaPdf && (
            <Link href={fileUrl(rutaPdf)} isExternal color={IMM_COLOR} fontSize="sm" fontWeight="bold">
              Ver documento guardado
            </Link>
          )}
        </HStack>

        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la canalización</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Dependencia destino</FormLabel>
          <Input value={f.dependenciaDestino || ''} onChange={(e) => set('dependenciaDestino', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Motivo</FormLabel>
          <Input value={f.motivo || ''} onChange={(e) => set('motivo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Decisión de denuncia</FormLabel>
          <Select placeholder="Sin especificar" value={f.decisionDenuncia || ''} onChange={(e) => set('decisionDenuncia', e.target.value)}>
            <option value="DeseaDenunciar">Desea denunciar</option>
            <option value="NoDeseaDenunciar">No desea denunciar</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Emitida'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Detalle</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Descripción del hecho</FormLabel>
          <Textarea rows={3} value={f.descripcionHecho || ''} onChange={(e) => set('descripcionHecho', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Solicitud</FormLabel>
          <Textarea rows={2} value={f.solicitud || ''} onChange={(e) => set('solicitud', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar canalización
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioCanalizacion;
